import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/constants";

export function MarketingHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t("marketing.product"), to: "/#product", id: "product" },
    { label: t("marketing.how"), to: "/#how", id: "how" },
    { label: t("marketing.forOrganisations"), to: "/#organisations", id: "organisations" },
    { label: t("marketing.forExperts"), to: "/#experts", id: "experts" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border/70">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a key={l.id} href={l.to} data-testid={`mk-nav-${l.id}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Button data-testid="mk-dashboard" onClick={() => navigate(roleHome(user))} className="rounded-lg active:scale-[0.98] transition-transform">
              {t("common.goToDashboard")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" data-testid="mk-login" onClick={() => navigate("/login")} className="rounded-lg">{t("common.signIn")}</Button>
              <Button data-testid="mk-register" onClick={() => navigate("/demo")} className="rounded-lg active:scale-[0.98] transition-transform">
                {t("common.bookDemo")}
              </Button>
            </>
          )}
        </div>
        <div className="flex md:hidden items-center gap-1">
          <LanguageSwitcher />
          <button data-testid="mk-mobile-toggle" className="grid place-items-center w-9 h-9" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background px-5 py-4 space-y-3">
          {LINKS.map((l) => <a key={l.id} href={l.to} onClick={() => setOpen(false)} className="block text-sm font-medium text-muted-foreground">{l.label}</a>)}
          <div className="flex gap-2 pt-2">
            {user ? (
              <Button className="flex-1 rounded-lg" onClick={() => navigate(roleHome(user))}>{t("common.dashboard")}</Button>
            ) : (
              <>
                <Button variant="outline" className="flex-1 rounded-lg" onClick={() => navigate("/login")}>{t("common.signIn")}</Button>
                <Button className="flex-1 rounded-lg" onClick={() => navigate("/demo")}>{t("common.bookDemo")}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border mt-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 flex flex-col sm:flex-row items-start justify-between gap-8">
        <div className="max-w-xs">
          <Logo />
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            {t("marketing.footerTagline")}
          </p>
        </div>
        <div className="flex gap-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("marketing.footerGetStarted")}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/register/company" className="hover:text-foreground">{t("marketing.footerRegisterCompany")}</Link></li>
              <li><Link to="/register/expert" className="hover:text-foreground">{t("marketing.footerApplyExpert")}</Link></li>
              <li><Link to="/login" className="hover:text-foreground">{t("common.signIn")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("marketing.footerLegal")}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">{t("marketing.footerPrivacyPolicy")}</Link></li>
              <li><Link to="/terms" className="hover:text-foreground" data-testid="footer-terms">{t("marketing.footerTerms")}</Link></li>
              <li><Link to="/account/privacy" className="hover:text-foreground">{t("marketing.footerPrivacyCentre")}</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 text-xs text-muted-foreground">
          {t("marketing.footerCopyright", { year: new Date().getFullYear() })}
        </div>
      </div>
    </footer>
  );
}
